/**
 * ErectionRecords — CR-2026-ERECTOR-E2 (2026-10-13). AISC 207-25 Ch 5 field
 * erection records: raising, fitting, field bolting, field welding, plumbing,
 * removal of temporary supports. Server: src/routes/erectionFieldRecords.js.
 * The server refuses records on a job without a released erection plan
 * (§5.12.6) and never edits or deletes a record; a wrong record is voided.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import {
  Container, Typography, Stack, Button, Paper, Chip, Alert, MenuItem, TextField, Checkbox, FormControlLabel,
  TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import PageGuide from "../../components/PageGuide";
import ApiCall from "../../utils/apicall";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";
import { apiMessage } from "../../utils/erectionPlansApi";

import SmartTable from "../../components/SmartTable/SmartTable";
const TYPES = {
  RAISING: "Raising", FITTING: "Fitting", FIELD_BOLTING: "Field bolting",
  FIELD_WELDING: "Field welding", PLUMBING: "Plumbing check", TEMP_SUPPORT_REMOVAL: "Temporary support removal",
};
const BOLT_METHODS = ["TURN_OF_NUT", "CALIBRATED_WRENCH", "TWIST_OFF", "DTI", "SNUG_TIGHT"];
const EMPTY = { record_type: "RAISING", piece_mark: "", location: "", description: "", result: "ACCEPTED", reject_reason: "",
  method: "TURN_OF_NUT", bolt_spec: "", bolt_count: "", wps: "", welder: "", within_tolerance: true, measured: "" };

function detailsFor(f) {
  if (f.record_type === "FIELD_BOLTING") return { method: f.method, bolt_spec: f.bolt_spec, bolt_count: f.bolt_count };
  if (f.record_type === "FIELD_WELDING") return { wps: f.wps, welder: f.welder };
  if (f.record_type === "PLUMBING") return { within_tolerance: f.within_tolerance, measured: f.measured };
  return undefined;
}
function detailText(r) {
  const d = r.details || {};
  if (r.record_type === "FIELD_BOLTING") return `${(d.method || "").replace(/_/g, " ").toLowerCase()}${d.bolt_spec ? ` · ${d.bolt_spec}` : ""}${d.bolt_count ? ` · ${d.bolt_count} bolts` : ""}`;
  if (r.record_type === "FIELD_WELDING") return `${d.wps || ""} · ${d.welder || ""}`;
  if (r.record_type === "PLUMBING") return `${d.within_tolerance ? "within tolerance" : "OUT of tolerance"}${d.measured ? ` · ${d.measured}` : ""}`;
  return "";
}

export default function ErectionRecords() {
  const { loginUser } = useSelector((state) => state.userSlice);
  const { permissions: perms = [] } = loginUser?.decodedToken ?? {};
  const canRecord = hasPermission(perms, PERMISSIONS.RECORD_FIELD_ERECTION);
  const canVoid = hasPermission(perms, PERMISSIONS.VOID_FIELD_ERECTION);

  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    ApiCall.get("/job").then((r) => setJobs(r?.data?.data?.jobs || [])).catch((e) => setMsg({ severity: "error", text: apiMessage(e, "Could not load jobs.") }));
  }, []);

  const load = useCallback(async () => {
    if (!jobId) { setRows([]); return; }
    try {
      const { data } = await ApiCall.get(`/erection-records?job_id=${jobId}${typeFilter ? `&type=${typeFilter}` : ""}`);
      setRows(data?.data?.records || []);
    } catch (e) { setMsg({ severity: "error", text: apiMessage(e, "Could not load records.") }); }
  }, [jobId, typeFilter]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  async function submit() {
    setBusy(true); setMsg(null);
    try {
      const { data } = await ApiCall.post("/erection-records", {
        job_id: Number(jobId), record_type: form.record_type, piece_mark: form.piece_mark, location: form.location,
        description: form.description, result: form.result,
        reject_reason: form.result === "REJECTED" ? form.reject_reason : undefined, details: detailsFor(form),
      });
      setMsg({ severity: data?.reminder ? "warning" : "success", text: data?.reminder || "Recorded." });
      setForm((f) => ({ ...EMPTY, record_type: f.record_type }));
      await load();
    } catch (e) { setMsg({ severity: "error", text: apiMessage(e, "Not recorded.") }); }
    finally { setBusy(false); }
  }

  async function voidRecord(r) {
    // eslint-disable-next-line no-alert
    const reason = window.prompt(`Void ${TYPES[r.record_type]} record for ${r.piece_mark}? Give the reason:`);
    if (!reason || !reason.trim()) return;
    try { await ApiCall.post(`/erection-records/${r.id}/void`, { reason }); await load(); }
    catch (e) { setMsg({ severity: "error", text: apiMessage(e, "Not voided.") }); }
  }

  return (
    <>
      <Helmet><title>Field Erection Records | SFM</title></Helmet>
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>Field Erection Records</Typography>
        <PageGuide
          why="Each raising, fit-up, field bolt, field weld, plumbing check and temporary support removal is recorded against the released erection plan."
          who="Field connectors, field welders and the job site foreman record; QM or foreman void a mistaken record with a reason."
          what="An append-only log per job. Records are never edited or deleted."
          how="Pick the job, choose the activity, enter the piece mark, location and result. Rejections need a reason and an NCR."
          when="As the work is done in the field."
          standard="AISC 207-25 Ch 5 · §5.12.6 · RCSC 2020 §8, §9 · AWS D1.1"
        />
        {msg && <Alert severity={msg.severity} sx={{ mb: 2 }}>{msg.text}</Alert>}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={2}>
            <TextField select size="small" label="Job" value={jobId} onChange={(e) => setJobId(e.target.value)} sx={{ minWidth: 300 }}>
              <MenuItem value="">Select a job…</MenuItem>
              {jobs.map((j) => <MenuItem key={j.id} value={j.id}>{j.name}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Activity" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} sx={{ minWidth: 220 }}>
              <MenuItem value="">All activities</MenuItem>
              {Object.entries(TYPES).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </TextField>
          </Stack>
        </Paper>

        {jobId && canRecord && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>New record</Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1 }}>
              <TextField select size="small" label="Activity" value={form.record_type} onChange={set("record_type")} sx={{ minWidth: 200 }}>
                {Object.entries(TYPES).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </TextField>
              <TextField size="small" label="Piece mark *" value={form.piece_mark} onChange={set("piece_mark")} />
              <TextField size="small" label="Location (grid / elevation)" value={form.location} onChange={set("location")} />
              <TextField select size="small" label="Result" value={form.result} onChange={set("result")} sx={{ minWidth: 150 }}>
                <MenuItem value="ACCEPTED">Accepted</MenuItem><MenuItem value="REJECTED">Rejected</MenuItem><MenuItem value="RECORDED">Recorded</MenuItem>
              </TextField>
            </Stack>
            {form.record_type === "FIELD_BOLTING" && (
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField select size="small" label="Pretension method *" value={form.method} onChange={set("method")} sx={{ minWidth: 220 }}>
                  {BOLT_METHODS.map((m) => <MenuItem key={m} value={m}>{m.replace(/_/g, " ").toLowerCase()}</MenuItem>)}
                </TextField>
                <TextField size="small" label="Bolt spec" value={form.bolt_spec} onChange={set("bolt_spec")} />
                <TextField size="small" label="Bolt count" value={form.bolt_count} onChange={set("bolt_count")} />
              </Stack>
            )}
            {form.record_type === "FIELD_WELDING" && (
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField size="small" label="WPS *" value={form.wps} onChange={set("wps")} />
                <TextField size="small" label="Welder *" value={form.welder} onChange={set("welder")} />
              </Stack>
            )}
            {form.record_type === "PLUMBING" && (
              <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
                <FormControlLabel control={<Checkbox checked={form.within_tolerance} onChange={set("within_tolerance")} />} label="Within tolerance" />
                <TextField size="small" label="Measured" value={form.measured} onChange={set("measured")} />
              </Stack>
            )}
            <TextField fullWidth size="small" label="Description" value={form.description} onChange={set("description")} sx={{ mb: 1 }} />
            {form.result === "REJECTED" && (
              <TextField fullWidth size="small" label="Reject reason *" value={form.reject_reason} onChange={set("reject_reason")} sx={{ mb: 1 }} />
            )}
            <Button variant="contained" disabled={busy || !form.piece_mark.trim()} onClick={submit}>Record</Button>
          </Paper>
        )}

        <TableContainer component={Paper}>
          <SmartTable tableId="erection-records" lockedColumns={["When", "Activity", "Piece", "Result"]} size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell><TableCell>Activity</TableCell><TableCell>Piece</TableCell><TableCell>Location</TableCell>
                <TableCell>Result</TableCell><TableCell>Details</TableCell><TableCell>Plan rev</TableCell><TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {jobId && rows.length === 0 && <TableRow><TableCell colSpan={8}>No records for this job yet.</TableCell></TableRow>}
              {rows.map((r) => (
                <TableRow key={r.id} sx={r.voided ? { opacity: 0.5 } : undefined}>
                  <TableCell>{new Date(r.recorded_at).toLocaleString()}</TableCell>
                  <TableCell>{TYPES[r.record_type]}</TableCell>
                  <TableCell>{r.piece_mark}</TableCell>
                  <TableCell>{r.location || "—"}</TableCell>
                  <TableCell>
                    {r.voided ? <Chip size="small" label={`Voided: ${r.void_reason}`} />
                      : <Chip size="small" color={r.result === "REJECTED" ? "error" : r.result === "ACCEPTED" ? "success" : "default"} label={r.result.toLowerCase()} />}
                    {r.reject_reason ? ` ${r.reject_reason}` : ""}
                  </TableCell>
                  <TableCell>{detailText(r)}</TableCell>
                  <TableCell>Rev {r.plan_revision}</TableCell>
                  <TableCell>{canVoid && !r.voided && <Button size="small" onClick={() => voidRecord(r)}>Void</Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </SmartTable>
        </TableContainer>
      </Container>
    </>
  );
}
